import { useState, useEffect } from 'react';
import { Shield, Cookie, FileText, ExternalLink, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { useAIConsent } from '@/hooks/useAIConsent';
import { supabase } from '@/integrations/supabase/client';
import { LEGAL_SUITE_VERSION } from '@/constants/legalSuite';
import { toast } from '@/hooks/use-toast';

const COOKIE_CONSENT_KEY = 'foco_cookie_consent';

export function PrivacySettings() {
  const { user } = useAuth();
  const { hasConsent: aiConsent, isLoading: aiLoading, grantConsent, revokeConsent } = useAIConsent();
  const [analytics, setAnalytics] = useState(false);
  const [functional, setFunctional] = useState(true);
  const [saving, setSaving] = useState(false);
  const [togglingAI, setTogglingAI] = useState(false);

  useEffect(() => {
    // Load from localStorage
    try {
      const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        setAnalytics(parsed.analytics ?? false);
        setFunctional(parsed.functional ?? true);
      }
    } catch { /* ignore */ }
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const consent = {
        essential: true,
        analytics,
        functional,
        consentedAt: new Date().toISOString(),
      };
      localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent));

      if (user) {
        await supabase.from('cookie_preferences').upsert({
          user_id: user.id,
          analytics_cookies: analytics,
          functional_cookies: functional,
          updated_at: new Date().toISOString(),
        }, { onConflict: 'user_id' });

        await supabase.from('consent_records').insert({
          user_id: user.id,
          consent_type: 'cookie_preferences_update',
          consent_version: LEGAL_SUITE_VERSION,
          accepted: true,
          user_agent: navigator.userAgent,
        });
      }

      toast({ title: 'Preferencias guardadas', description: 'Tus preferencias de privacidad han sido actualizadas.' });
    } catch (e) {
      toast({ title: 'Error', description: 'No se pudieron guardar las preferencias.', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Shield className="h-4 w-4 text-primary" />
          Privacidad y Cookies
        </CardTitle>
        <CardDescription>Gestiona tus preferencias de privacidad y consentimiento</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Cookie toggles */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Cookies esenciales</p>
              <p className="text-xs text-muted-foreground">Siempre activas</p>
            </div>
            <Switch checked disabled />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Cookies funcionales</p>
              <p className="text-xs text-muted-foreground">Tema, moneda, gráficos</p>
            </div>
            <Switch checked={functional} onCheckedChange={setFunctional} />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Cookies analíticas</p>
              <p className="text-xs text-muted-foreground">Mejora del servicio</p>
            </div>
            <Switch checked={analytics} onCheckedChange={setAnalytics} />
          </div>
        </div>

        <Button size="sm" onClick={handleSave} disabled={saving} className="w-full">
          {saving ? 'Guardando...' : 'Guardar preferencias'}
        </Button>

        {/* AI Consent Toggle */}
        <div className="pt-3 border-t space-y-2">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1">
            <Sparkles className="h-3 w-3" /> Inteligencia Artificial
          </p>
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-0.5">
              <p className="text-sm font-medium">Usar asesoría con inteligencia artificial con mis datos financieros</p>
              <p className="text-xs text-muted-foreground">
                Si desactivas esta opción, FOCO dejará de enviar tus mensajes y tu información financiera a servicios de IA de terceros y se desactivarán el chat IA y los tips personalizados.
              </p>
            </div>
            <Switch
              checked={aiConsent === true}
              disabled={aiLoading || togglingAI}
              onCheckedChange={async (checked) => {
                setTogglingAI(true);
                try {
                  const ok = checked ? await grantConsent() : await revokeConsent();
                  if (ok) {
                    toast({
                      title: checked ? 'IA activada' : 'IA desactivada',
                      description: checked
                        ? 'Ahora puedes usar el asesor financiero con IA.'
                        : 'Se ha desactivado el uso de IA con tus datos financieros.',
                    });
                  }
                } finally {
                  setTogglingAI(false);
                }
              }}
            />
          </div>
        </div>

        {/* Legal links */}
        <div className="pt-3 border-t space-y-2">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Documentos legales</p>
          <div className="grid grid-cols-2 gap-2">
            <Link to="/privacy-policy" className="text-xs text-primary hover:underline flex items-center gap-1">
              <FileText className="h-3 w-3" /> Privacidad
            </Link>
            <Link to="/terms-and-conditions" className="text-xs text-primary hover:underline flex items-center gap-1">
              <FileText className="h-3 w-3" /> Términos
            </Link>
            <Link to="/cookies-policy" className="text-xs text-primary hover:underline flex items-center gap-1">
              <Cookie className="h-3 w-3" /> Cookies
            </Link>
            <Link to="/consent-information" className="text-xs text-primary hover:underline flex items-center gap-1">
              <ExternalLink className="h-3 w-3" /> Consentimiento
            </Link>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
